/**
 * One-tap "Thank back" mini composer. Shown to the ORIGINAL sender after
 * the wall owner accepts their card. Reuses POST /api/gratitude/offer with
 * `acknowledges_card_id` set so the original card gets marked thanked_back.
 */

import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import confetti from "canvas-confetti";
import { Dialog, DialogContent } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "./ui/avatar";
import { Heart, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { LinkedAvatar, LinkedName } from "./LinkedProfile";

const API = "/api";

// Rotate through prompts so the UI feels fresh each open
const PROMPTS = [
  "the way you…",
  "that time you…",
  "how you always…",
  "something small that mattered…",
];

const fireHearts = () => {
  confetti({
    particleCount: 50,
    spread: 50,
    startVelocity: 25,
    origin: { y: 0.5 },
    colors: ["#c4b5fd", "#f0abfc", "#fbcfe8", "#a78bfa"],
    shapes: ["circle"],
    scalar: 0.8,
  });
};

const ThankBackSheet = ({ open, onOpenChange, targetName, targetPicture, targetUsername, connectionId, originalCardId, onSuccess }) => {
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [prompt, setPrompt] = useState(PROMPTS[0]);

  useEffect(() => {
    if (open) {
      setPrompt(PROMPTS[Math.floor(Math.random() * PROMPTS.length)]);
      setContent("");
      setDone(false);
    }
  }, [open]);

  const submit = async () => {
    if (!content.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/gratitude/offer`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          connection_id: connectionId,
          content: content.trim(),
          paper_color: "violet",
          visibility: "circle",
          acknowledges_card_id: originalCardId,
        }),
      });
      if (!res.ok) throw new Error("send failed");
      setDone(true);
      setTimeout(fireHearts, 80);
      setTimeout(() => {
        onOpenChange(false);
        onSuccess?.();
      }, 1600);
    } catch {
      toast.error("Couldn't send — try again");
    } finally {
      setSubmitting(false);
    }
  };

  const firstName = (targetName || "them").split(" ")[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden" data-testid="thank-back-sheet">
        <AnimatePresence mode="wait">
          {!done ? (
            <motion.div
              key="compose"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="p-6"
            >
              <div className="flex items-center gap-2.5 mb-4">
                <Heart className="w-4 h-4 text-violet-600 fill-violet-600" />
                <h2 className="text-base font-semibold text-slate-900">
                  Thank {firstName} back
                </h2>
              </div>
              <p className="text-xs text-slate-500 mb-4">
                Something small, just for them.
              </p>

              <div className="flex items-start gap-3 mb-3">
                <Avatar className="h-9 w-9 flex-shrink-0 mt-1">
                  <AvatarImage src={targetPicture} alt={targetName} />
                  <AvatarFallback className="text-xs bg-violet-100 text-violet-600">
                    {firstName.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <Textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder={`I'm grateful for ${prompt}`}
                  rows={4}
                  maxLength={600}
                  className="resize-none text-sm"
                  autoFocus
                  data-testid="thank-back-textarea"
                />
              </div>

              <div className="flex items-center justify-between gap-2 pt-2">
                <span className="text-[10px] text-slate-400">
                  Kept in your Circle — {firstName} decides what becomes public
                </span>
                <Button
                  onClick={submit}
                  disabled={submitting || !content.trim()}
                  className="bg-violet-600 hover:bg-violet-700 text-white rounded-full text-xs px-4"
                  data-testid="thank-back-send"
                >
                  {submitting ? "Sending…" : "Send"}
                </Button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="done"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="p-10 text-center"
            >
              <motion.div
                initial={{ scale: 0, rotate: -20 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 260, damping: 14 }}
                className="w-14 h-14 rounded-full bg-gradient-to-br from-violet-400 to-fuchsia-500 mx-auto flex items-center justify-center mb-3"
              >
                <Sparkles className="w-7 h-7 text-white" />
              </motion.div>
              <p className="text-base font-semibold text-slate-900 mb-1">Sent to {firstName}.</p>
              <p className="text-xs text-slate-500">A small good thing.</p>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

export default ThankBackSheet;
